import React, { useEffect, useRef, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { useHandleProfileData } from '@pkg/hooks';
import { TextArea, type TextAreaHookFn, type TextAreaHookOptions, useTextArea } from '@pkg/primitives';
import { useMediaQuery } from '@pkg/providers';

import { i18nUsernameValidationKeys, USERNAME_LENGTH, validateUsername, validateUsernameLength } from '@app/helpers';

import { CharacterCount } from '../CharacterCount';
import { EditIcon } from '../EditIcon';
import { ErrorMessage } from '../ErrorMessage';

import { MirrorText, StyledUserName, TextAreaWrapper } from './UserName.style';
import { PLACEHOLDER, UNIQUENESS_ERROR } from './UserName.tokens';

export interface UserNameProps {
    initialValue?: TextAreaHookOptions['initialValue'];
}

export function UserName(props: UserNameProps) {
    // Value could be `null`, so we are avoiding destructure/assignment.
    const initialValue = props.initialValue ? `${props.initialValue}` : '';

    const [translate] = useTranslation('fantasy_wishlist');
    const { tablet } = useMediaQuery();

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const lastSavedRef = useRef(initialValue);
    const interactedRef = useRef(false);

    const [errorKey, setErrorKey] = useState('');
    const [showEditIcon, setShowEditIcon] = useState(false);
    const [showDuplicateError, setShowDuplicateError] = useState(false);

    const { matchingUsername, updateUsername } = useHandleProfileData();

    function handleTextAreaEnter() {
        setShowEditIcon(true);
    }

    function handleTextAreaLeave() {
        setShowEditIcon(false);
    }

    const onBlur: TextAreaHookFn = (capturedValue) => {
        const usernameAlreadyClaimed = matchingUsername === capturedValue;
        const hasChanged = capturedValue !== lastSavedRef.current;
        const validated = validateUsername(capturedValue);

        const shouldUpdate = Boolean(capturedValue.length && hasChanged && validated && !usernameAlreadyClaimed);

        setShowDuplicateError(usernameAlreadyClaimed);

        // If preferred, we can call `handleReset()` for any `!shouldUpdate` condition.
        if (!shouldUpdate && capturedValue === '') {
            handleReset();
        }

        if (shouldUpdate) {
            lastSavedRef.current = capturedValue;
            updateUsername(capturedValue);
        }
    };

    const onFocus: TextAreaHookFn = (_capturedValue) => {
        interactedRef.current = true;
        setShowDuplicateError(false);
    };

    const onEnter: TextAreaHookFn = (_capturedValue) => {
        textareaRef.current?.blur();
    };

    const { value, mirrorValue, focused, handleBlur, handleFocus, handleChange, handleReset } = useTextArea({
        placeholder: PLACEHOLDER,
        filtered: true,
        minLength: USERNAME_LENGTH.min,
        maxLength: USERNAME_LENGTH.max,
        initialValue,
        onBlur,
        onFocus,
        onEnter,
    });

    // Check against `interactedRef` to prevent error message on first mount.
    const hasError = interactedRef.current && Boolean(errorKey || showDuplicateError);

    const editIconMobile =
        !tablet && !focused ? <EditIcon key="UserName-EditIcon-mobile" subdued={!showEditIcon} /> : null;

    const editIconDesktop = tablet && showEditIcon && !focused ? <EditIcon key="UserName-EditIcon-desktop" /> : null;

    const characterCountMarkup =
        focused && !validateUsernameLength(value) ? (
            <CharacterCount key="UserName-CharacterCount" count={value.length} error={hasError} />
        ) : null;

    const errorMarkup = hasError ? (
        <ErrorMessage
            key="UserName-ErrorMessage"
            message={showDuplicateError ? UNIQUENESS_ERROR : translate(errorKey)}
        />
    ) : null;

    // Edit Icon toggle on media query change
    useEffect(() => {
        if (!tablet) handleTextAreaLeave();
    }, [tablet]);

    // Error validation effect
    useEffect(() => {
        const messages = i18nUsernameValidationKeys(value);

        setErrorKey(messages[0] ?? '');
        setShowDuplicateError(matchingUsername === value);
    }, [matchingUsername, value]);

    return (
        <StyledUserName>
            <TextAreaWrapper onPointerEnter={handleTextAreaEnter} onPointerLeave={handleTextAreaLeave}>
                <TextArea
                    ref={textareaRef}
                    value={value}
                    placeholder={PLACEHOLDER}
                    ariaLabel="Enter a username for your profile"
                    error={hasError}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    onChange={handleChange}
                    // Not passing `min/max` values because we prefer
                    // the UX of allowing the user to input anything
                    // and validate as they type.
                    // minLength={USERNAME_LENGTH.min}
                    // maxLength={USERNAME_LENGTH.max}
                />

                <MirrorText aria-hidden="true">{mirrorValue}</MirrorText>

                <AnimatePresence>
                    {editIconMobile}
                    {editIconDesktop}
                    {characterCountMarkup}
                </AnimatePresence>
            </TextAreaWrapper>

            <AnimatePresence>{errorMarkup}</AnimatePresence>
        </StyledUserName>
    );
}
